import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, query, collection, where, getDocs } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch profile by UID, with optional email fallback + auto-migration
  async function fetchProfile(uid, emailFallback) {
    // Step 1: Try by UID (fast path, works after first Google login)
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setUserProfile(docSnap.data());
      return docSnap.data();
    }

    // Step 2: If not found by UID, try by email (handles existing accounts)
    if (emailFallback) {
      const emailLower = emailFallback.toLowerCase();
      const q = query(collection(db, 'users'), where('email', '==', emailLower));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const existingProfile = snap.docs[0].data();
        // Migrate to new UID so future logins resolve instantly
        const updatedProfile = { ...existingProfile, uid };
        await setDoc(doc(db, 'users', uid), updatedProfile);
        if (existingProfile.username) {
          await setDoc(doc(db, 'usernames', existingProfile.username), { uid });
        }
        setUserProfile(updatedProfile);
        return updatedProfile;
      }
    }

    return null;
  }

  async function checkUsernameAvailable(username) {
    const q = query(collection(db, 'users'), where('username', '==', username.toLowerCase()));
    const snapshot = await getDocs(q);
    return snapshot.empty;
  }

  async function createProfile(uid, email, data) {
    const profile = {
      uid,
      email: email.toLowerCase(),
      username: data.username.toLowerCase(),
      displayName: data.displayName || '',
      firstName: data.firstName || '',
      securityQuestion: data.securityQuestion || '',
      securityAnswer: data.securityAnswer || '',
      pin: data.pin || '',
      createdAt: new Date().toISOString(),
      shareSlug: data.username.toLowerCase(),
      careerTeamEmails: [],
      friendEmails: [],
      network1: { name: 'Network 1', emails: [], permissions: { stats: true, companies: true, roles: true, contacts: false, notes: false } },
      network2: { name: 'Network 2', emails: [], permissions: { stats: true, companies: true, roles: true, contacts: false, notes: false } },
      accessCode: { code: '', permissions: { stats: true, companies: true, roles: true, contacts: false, notes: false }, enabled: true },
      publicEnabled: true,
      quickPadsMode: 'compact',
      showNotifications: true,
      highlightEnabled: false,
      showSearchTip: true,
      publicPermissions: { stats: true, companies: true, roles: true, contacts: false, notes: false },
      networks: [],
      showDates: true,
      standaloneContacts: [],
      searchPlan: '',
      generalIdeas: '',
      searchPlanMode: 'plain',
      generalIdeasMode: 'plain',
      templates: [
        { label: 'Cold Email', content: '' },
        { label: 'Follow Up', content: '' },
        { label: 'Thank You', content: '' },
        { label: 'Networking', content: '' },
        { label: 'Custom', content: '' }
      ],
      quickNotes: ''
    };
    await setDoc(doc(db, 'users', uid), profile);
    await setDoc(doc(db, 'usernames', data.username.toLowerCase()), { uid });
    setUserProfile(profile);
    return profile;
  }

  async function signInWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    // Pass email as fallback so existing accounts are found even if UID differs
    const profile = await fetchProfile(result.user.uid, result.user.email);
    return { user: result.user, profile, isNew: !profile };
  }

  async function signUpWithEmail(email, password) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  }

  async function signInWithEmail(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const profile = await fetchProfile(result.user.uid);
    return { user: result.user, profile };
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Pass email as fallback for Google users whose UID may not match yet
        await fetchProfile(user.uid, user.email);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    setUserProfile,
    loading,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    logout,
    createProfile,
    checkUsernameAvailable,
    fetchProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
